import { AUTH_HEADERS, isLocaleCA } from '@libs/core';
import { getENV } from '@libs/common-next';
import { createApiProxyClient, getAccessToken, getAnonymousIdToken, getCustomerKey, getVisitorId } from '../utils';
import type { ApiProxyClientOptions } from '../types';

function requestBuilder<TBody = BodyInit>(options: ApiProxyClientOptions<TBody> = {}): ApiProxyClientOptions<TBody> {
    const headers: HeadersInit = {};

    const accessToken = getAccessToken();
    const customerKey = getCustomerKey();
    const anonymousKey = getAnonymousIdToken();
    const visitorId = getVisitorId();

    if (accessToken) {
        headers[AUTH_HEADERS.AUTHORIZATION] = `Bearer ${accessToken}`;
    }

    if (customerKey) {
        headers[AUTH_HEADERS.X_PETM_CUSTOMER_KEY] = customerKey;
    }

    if (anonymousKey && !customerKey) {
        headers[AUTH_HEADERS.X_PETM_ANONYMOUS_KEY] = anonymousKey;
    }

    if (visitorId) {
        headers[AUTH_HEADERS.X_PETM_VISITOR_ID] = visitorId;
    }

    const ephemeralEnv = getENV('NEXT_PUBLIC_EPHEMERAL');

    return {
        ...options,
        headers: {
            ...options.headers,
            ...headers,
            ...(ephemeralEnv && { 'petm-eph-env': ephemeralEnv }),
        },
    };
}

let _cweaClient: ReturnType<typeof createApiProxyClient> | null = null;
let _identityClient: ReturnType<typeof createApiProxyClient> | null = null;

function initCweaClient() {
    if (!_cweaClient) {
        const basePath = isLocaleCA()
            ? getENV('NEXT_PUBLIC_CWEA_PROXY_URL_CA')
            : getENV('NEXT_PUBLIC_CWEA_PROXY_URL_US');

        _cweaClient = createApiProxyClient(basePath, requestBuilder);
    }

    return _cweaClient;
}

function initIdentityClient() {
    if (!_identityClient) {
        const basePath = isLocaleCA()
            ? getENV('NEXT_PUBLIC_IDENTITY_API_PROXY_URL_CA')
            : getENV('NEXT_PUBLIC_IDENTITY_API_PROXY_URL_US');

        _identityClient = createApiProxyClient(basePath, requestBuilder);
    }

    return _identityClient;
}

// Using a Proxy to lazily initialize the `client` object only when a method is called.
// This avoids the immediate execution of `getENV(...)` (which can throw an error if an env var is missing) at module load time.
// The Proxy ensures that the client is initialized on-demand, allowing for proper testing and environment setup,
// while maintaining a clean API for consumers who can use `client.get(...)` directly without needing to call `client()` first.
export const cweaClient = new Proxy({} as ReturnType<typeof createApiProxyClient>, {
    get(_target, prop) {
        return initCweaClient()[prop as keyof typeof _cweaClient];
    },
});

export const identityClient = new Proxy({} as ReturnType<typeof createApiProxyClient>, {
    get(_target, prop) {
        return initIdentityClient()[prop as keyof typeof _identityClient];
    },
});
